import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

// Material
import { makeStyles } from "@material-ui/core/styles";
import { Grid } from "@material-ui/core";

// Config
import { externalRoutes } from "src/config/routes";
import { getStatementUrl } from "src/config/apiConfig";
import { statementRequestSources, statementStatus } from "src/config/values";
import styles from "src/config/styles";

// Hooks
import useAuthorizedContent from "src/hooks/useAuthorizedContent";
import useTranslate from "src/hooks/useTranslate";

// Actions
import { parseFetchOptions } from "src/state/user/actions";

// Utils
import statements from "src/utils/statements";

// Components
import ErrorPage from "src/containers/ErrorPage";
import Link from "src/components/Link";
import LoadingSection from "src/components/LoadingSection";
import MainSection from "src/components/MainSection";
import Text from "src/components/Text";
import SummaryCard from "./components/SummaryCard";
import ConfirmationCard from "./components/ConfirmationCard";
import StatementForm from "./components/StatementForm";

const useStyles = makeStyles(theme => ({
  gridContainer: {
    maxWidth: "790px",
    padding: theme.spacing(3, 0),
    gap: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(2)
    }
  },
  root: {
    display: "flex",
    flexDirection: "column",
    transition: "padding-top .3s ease-in-out",
    alignItems: "center",
    width: "100%",
    paddingBottom: theme.spacing(15)
  },
  link: {
    ...styles.mixins.link,
    textTransform: "lowercase"
  }
}));

const StatementPage = () => {
  const classes = useStyles();
  const [statement, setStatement] = useState(null);
  const [isCompleted, setCompleted] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { caseId, itemId } = useParams();
  const dispatch = useDispatch();
  const getText = useTranslate();

  useAuthorizedContent();

  const handleComplete = () => {
    setCompleted(true);
  };

  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      setError(null);
    }

    dispatch(
      parseFetchOptions(({ headers: { Authorization, ...headers } }) => {
        const url = getStatementUrl(caseId, itemId);
        const options = {
          method: "GET",
          headers: { ...headers, Authorization }
        };
        fetch(url, options)
          .then(res => {
            return res.json();
          })
          .then(data => {
            if (isMounted) {
              setStatement(data);
            }
          })
          .catch(err => {
            if (isMounted) {
              setError(err);
            }
          })
          .finally(() => {
            if (isMounted) {
              setLoading(false);
            }
          });
      })
    );
    return () => {
      isMounted = false;
    };
  }, []);

  if (error)
    return (
      <MainSection className={classes.root}>
        <ErrorPage />
      </MainSection>
    );

  const isSai =
    statement?.requestSource === statementRequestSources.SELF_ADMINISTERED;

  return (
    <MainSection className={classes.root}>
      {isLoading ? (
        <LoadingSection />
      ) : (
        <Grid
          container
          justifyContent="center"
          direction="column"
          alignItems="center"
          className={classes.gridContainer}
        >
          <SummaryCard
            isSai={isSai}
            organizationId={statement.organizationId}
            requester={statement.requester}
          />
          {isCompleted || statement.state === statementStatus.COMPLETED ? (
            <ConfirmationCard isSai={isSai} />
          ) : (
            <StatementForm
              isSai={isSai}
              onComplete={handleComplete}
              responses={statement.responses}
              schema={statements.getStatementSchema(statement)}
            />
          )}
          <div>
            <Text
              type="subtitle1Small"
              align="center"
              text="downloadReportPage.havingIssues"
              values={{
                1: (
                  <Link
                    key={1}
                    className={classes.link}
                    target="_blank"
                    to={externalRoutes.contact}
                  >
                    {getText("downloadReportPage.contactSupportLink")}
                  </Link>
                ),
                isComponent: true
              }}
            />
          </div>
        </Grid>
      )}
    </MainSection>
  );
};

export default StatementPage;
